import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = ["B2B", "B2G", "Other"];
const INDUSTRIES = [
  "Public Sector / Defense",
  "Financial Services",
  "Healthcare & Life Sciences",
  "Energy & Utilities",
  "Logistics & Mobility",
  "Telecom & Technology",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    sector: "B2B",
    industry: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.company || !form.email || !form.message) {
      toast.error("Please complete name, company, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Briefing request received. We will respond within one business day.");
      setForm({ name: "", company: "", email: "", sector: "B2B", industry: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : typeof detail === "string"
        ? detail
        : "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full h-12 px-4 bg-white border border-neutral-300 text-black placeholder:text-neutral-400 focus:border-black focus:outline-none transition-colors rounded-none font-medium";

  return (
    <section id="contact" data-testid="contact-section" className="bg-neutral-50 border-b border-neutral-200">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-20 md:py-32 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <span className="font-mono-tech text-xs tracking-[0.2em] uppercase text-neutral-500">
            [07] Engage
          </span>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
            Brief us in
            <br />
            <span className="text-[#002FA7]">eight lines</span>.
          </h2>
          <p className="mt-6 text-neutral-700 leading-relaxed max-w-md">
            Tell us the problem and the deadline. A senior practitioner — not a sales pod —
            responds within one business day with an honest read.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-0 border-t border-l border-neutral-200 max-w-md">
            <div className="border-r border-b border-neutral-200 p-4">
              <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500">Email</div>
              <div className="mt-2 font-display text-sm">briefings@datavex.io</div>
            </div>
            <div className="border-r border-b border-neutral-200 p-4">
              <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500">Offices</div>
              <div className="mt-2 font-display text-sm">Washington · NYC · London</div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="col-span-12 lg:col-span-7 bg-white border border-neutral-300 p-6 md:p-10"
        >
          {done && (
            <div data-testid="contact-success" className="mb-6 p-4 border border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] font-medium text-sm">
              Briefing request received. A practitioner will respond within one business day.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-2">
                Name
              </label>
              <input
                id="name"
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className={inputBase}
                placeholder="Jane Holloway"
                autoComplete="name"
              />
            </div>
            <div>
              <label htmlFor="company" className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-2">
                Company / Agency
              </label>
              <input
                id="company"
                data-testid="contact-company"
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                className={inputBase}
                placeholder="Northbank Capital"
                autoComplete="organization"
              />
            </div>
            <div>
              <label htmlFor="email" className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-2">
                Work Email
              </label>
              <input
                id="email"
                type="email"
                data-testid="contact-email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className={inputBase}
                placeholder="jane@northbank.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-2">
                Sector
              </label>
              <Select value={form.sector} onValueChange={(v) => update("sector", v)}>
                <SelectTrigger
                  data-testid="contact-sector"
                  className="w-full h-12 rounded-none border-neutral-300 bg-white text-black focus:ring-0 focus:border-black px-4 font-medium"
                >
                  <SelectValue placeholder="Select sector" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {SECTORS.map((s) => (
                    <SelectItem key={s} value={s} className="rounded-none" data-testid={`sector-opt-${s.toLowerCase()}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-2">
                Industry (optional)
              </label>
              <Select value={form.industry || undefined} onValueChange={(v) => update("industry", v)}>
                <SelectTrigger
                  data-testid="contact-industry"
                  className="w-full h-12 rounded-none border-neutral-300 bg-white text-black focus:ring-0 focus:border-black px-4 font-medium"
                >
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {INDUSTRIES.map((s) => (
                    <SelectItem key={s} value={s} className="rounded-none">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="message" className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-2">
                Brief
              </label>
              <textarea
                id="message"
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={5}
                placeholder="Problem, current data stack, and the decision you need to support…"
                className={`${inputBase} h-auto py-3 resize-y`}
              />
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
            <p className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-neutral-500 max-w-xs">
              By submitting you agree to our brief retention policy. We never share inquiries.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="group inline-flex items-center gap-3 px-6 h-12 bg-black text-white text-sm font-semibold hover:bg-[#002FA7] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? "Sending…" : "Send Briefing"}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
